import { fullDaysOfWeek } from '@constants/Common/date.constant'
import {
  CallRecordingMode,
  IClinic,
  IClinicNumber,
  IWorkingHours,
} from '@dataTypes/clinic.type'

const DEFAULT_PDF_CLINIC_NAME = 'EnamelAI Clinic'

export interface ClinicPdfAddressFields {
  clinic_name?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
}

export interface ClinicPdfHeaderPayload {
  clinicName: string
  clinicAddress?: string
}

/** Single-line clinic address for PDF headers (matches kiosk formatting). */
export const formatClinicAddressForPdf = (
  clinic: ClinicPdfAddressFields,
): string => {
  const segments: string[] = []
  const street = (clinic.address ?? '').trim()
  if (street.length > 0) {
    segments.push(street)
  }

  const cityStateZip = [clinic.city, clinic.state, clinic.zip]
    .map((value) => (value ?? '').trim())
    .filter((value) => value.length > 0)
    .join(', ')
  if (cityStateZip.length > 0) {
    segments.push(cityStateZip)
  }

  return segments.join(', ')
}

export const buildClinicPdfHeaderPayload = (
  clinic: ClinicPdfAddressFields | null | undefined,
): ClinicPdfHeaderPayload => {
  if (!clinic) {
    return { clinicName: DEFAULT_PDF_CLINIC_NAME }
  }

  const clinicName = clinic.clinic_name?.trim() || DEFAULT_PDF_CLINIC_NAME
  const clinicAddress = formatClinicAddressForPdf(clinic)
  return clinicAddress.length > 0
    ? { clinicName, clinicAddress }
    : { clinicName }
}

export const getClinicNumber = (clinic: IClinic): string => {
  let clinicNumber = ''
  if (clinic.clinic_number && clinic.clinic_number.length > 0) {
    clinicNumber =
      clinic.clinic_number.find((number) => number.status === 'Active')
        ?.phone_number || ''
  }
  return clinicNumber
}

export const getActiveMainClinicNumber = (
  clinic: IClinic,
  functionality?: string,
): string => {
  let numbers = clinic.clinic_number
  if (functionality) {
    numbers = numbers?.filter((n) => n.contact_methods?.includes(functionality))
  }
  if (numbers?.length) {
    const main = numbers.find(
      (n) => n.status === 'Active' && n.is_main === true,
    )
    if (main?.phone_number) {
      return main.phone_number.trim()
    }
    const fallbackActive = getClinicNumber(clinic)
    if (fallbackActive) {
      return fallbackActive.trim()
    }
  }
  return (clinic.phone_number || '').trim()
}

export interface ClinicContactPhoneSource {
  phone_number?: string | null
  clinic_number?: Array<{
    phone_number?: string | null
    status?: string
    is_main?: boolean
    contact_methods?: string | null
  }> | null
}

/** Active main clinic number, then any active number, then clinic.phone_number. */
export const getClinicContactPhone = (
  clinic: ClinicContactPhoneSource | null | undefined,
): string => {
  if (!clinic) return ''
  return getActiveMainClinicNumber({
    phone_number: clinic.phone_number ?? '',
    clinic_number: (clinic.clinic_number ?? []) as IClinic['clinic_number'],
  } as IClinic)
}

const DEFAULT_TZ = 'America/Los_Angeles'

export const buildWorkingHoursPayload = (
  activeNumber: IClinicNumber,
  lunch: IWorkingHours,
  overrides: IWorkingHours[],
  timezone: string,
): IWorkingHours[] => {
  const calendar = (activeNumber.workingHours ?? []).filter((wh) =>
    fullDaysOfWeek.includes(wh.day_of_week),
  )
  const lunchRow: IWorkingHours = {
    ...lunch,
    day_of_week: 'lunch_break',
    entity_type: 'clinic_number',
    entity_id: activeNumber.clinic_number_id ?? '',
    name: lunch.name,
  }
  const tz = timezone || DEFAULT_TZ
  return [...calendar, lunchRow, ...overrides].map((wh) => ({
    ...wh,
    entity_id: activeNumber.clinic_number_id ?? '',
    entity_type: 'clinic_number' as const,
    working_hours_id: undefined,
    timezone: wh.timezone || tz,
    name: wh.name,
  }))
}

export const clonePayloadForClinicNumberId = (
  workingHours: IWorkingHours[],
  clinicNumberId: string,
  timezone: string,
): IWorkingHours[] => {
  const tz = timezone || DEFAULT_TZ
  return workingHours.map((wh) => ({
    ...wh,
    entity_id: clinicNumberId,
    working_hours_id: undefined,
    timezone: wh.timezone || tz,
  }))
}

export const toWorkingHoursReplaceRow = (
  wh: IWorkingHours,
): {
  day_of_week: string
  timezone: string
  name: string
  start_time: string
  end_time: string
} => {
  return {
    day_of_week: wh.day_of_week,
    timezone: (wh.timezone as string) || DEFAULT_TZ,
    name: (wh.name as string) ?? '',
    start_time: wh.start_time,
    end_time: wh.end_time,
  }
}

export const toWorkingHoursReplaceRows = (
  list: IWorkingHours[],
): ReturnType<typeof toWorkingHoursReplaceRow>[] => {
  return list.map(toWorkingHoursReplaceRow)
}

export const callRecordingModeFromBooleans = (
  inbound?: boolean,
  outbound?: boolean,
): CallRecordingMode => {
  if (inbound && outbound) return 'both'
  if (inbound) return 'inbound'
  if (outbound) return 'outbound'
  return 'none'
}

export const callRecordingModeToBooleans = (
  mode: CallRecordingMode,
): { record_inbound_calls: boolean; record_outbound_calls: boolean } => ({
  record_inbound_calls: mode === 'inbound' || mode === 'both',
  record_outbound_calls: mode === 'outbound' || mode === 'both',
})

export const formatCallRecordingMode = (mode: CallRecordingMode): string[] => {
  switch (mode) {
    case 'both':
      return ['Inbound', 'Outbound']
    case 'inbound':
      return ['Inbound']
    case 'outbound':
      return ['Outbound']
    default:
      return []
  }
}
